import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Tooltip
} from '@material-ui/core';
import { Edit, Visibility, GetApp, Refresh, Add } from '@material-ui/icons';
import { Main } from 'src/components/commons/Main';
import { exportTableToCSV } from 'src/helpers/csvExport';
import { Alert } from '@material-ui/lab';
import { 
  getFounderFunderMatchesAdmin, 
  getFounderFunderMatchStatsAdmin,
  updateFounderFunderMatchAdmin,
  addInvestmentDetailsAdmin,
  trackCommunicationAdmin
} from 'src/api/matches';
import { format } from 'date-fns';

// Status and stage mappings
const statusMap = {
  'MATCHED': { label: 'Matched', color: 'default' },
  'CONNECTED': { label: 'Connected', color: 'primary' },
  'INVESTMENT_DISCUSSION': { label: 'Investment Discussion', color: 'secondary' },
  'INVESTMENT_COMMITTED': { label: 'Investment Committed', color: 'warning' },
  'INVESTMENT_COMPLETED': { label: 'Investment Completed', color: 'success' },
  'DISCONNECTED': { label: 'Disconnected', color: 'error' }
};

const connectionMethodMap = {
  'auto_accept': { label: 'Auto Accept', color: 'default' },
  'manual': { label: 'Manual', color: 'primary' },
  'onboarding_form': { label: 'Onboarding Form', color: 'secondary' }
};

const investmentStageMap = {
  'initial_contact': { label: 'Initial Contact', color: 'default' },
  'due_diligence': { label: 'Due Diligence', color: 'primary' },
  'term_sheet': { label: 'Term Sheet', color: 'secondary' },
  'negotiation': { label: 'Negotiation', color: 'warning' },
  'closing': { label: 'Closing', color: 'info' },
  'post_investment': { label: 'Post Investment', color: 'success' }
};

export default function FounderFunderMatches() {
  // Get user context from Redux store
  const { authUser } = useSelector(({ auth }) => auth);
  
  // State management
  const [matches, setMatches] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    connectionMethod: '',
    investmentStage: '',
    matchedAtFrom: '',
    matchedAtTo: '',
    minMatchScore: '',
    maxMatchScore: ''
  });

  // Dialog states
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [investmentDialogOpen, setInvestmentDialogOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch matches data
  const fetchMatches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 [DEBUG] Component: fetchMatches called with:', { filters, page: page + 1, rowsPerPage });
      
      const response = await getFounderFunderMatchesAdmin(filters, { 
        page: page + 1, 
        limit: rowsPerPage 
      }, authUser);
      
      console.log('🔍 [DEBUG] Component: API response received:', response);
      console.log('🔍 [DEBUG] Component: Response type:', typeof response);
      console.log('🔍 [DEBUG] Component: Response keys:', response ? Object.keys(response) : 'No response');
      console.log('🔍 [DEBUG] Component: Response.data:', response.data);
      console.log('🔍 [DEBUG] Component: Response.total:', response.total);
      // The response is the actual array, not wrapped in an object
      // So we need to handle it differently
      if (Array.isArray(response)) {
        console.log('🔍 [DEBUG] Component: Response is an array, setting matches directly');
        setMatches(response);
        setTotalCount(response.length);
      } else if (response && response.data) {
        console.log('🔍 [DEBUG] Component: Response has data property, using it');
        setMatches(response.data || []);
        setTotalCount(response.total || 0);
      } else {
        console.log('🔍 [DEBUG] Component: Unexpected response structure, setting empty');
        setMatches([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('🔍 [DEBUG] Component: Error fetching matches:', err);
      setError('Failed to load matches');
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      console.log('🔍 [DEBUG] Component: fetchStats called');
      const response = await getFounderFunderMatchStatsAdmin(authUser);
      console.log('🔍 [DEBUG] Component: Stats response received:', response);
      // Handle both array and object responses for stats
      if (Array.isArray(response)) {
        console.log('🔍 [DEBUG] Component: Stats response is an array, setting empty stats');
        setStats({});
      } else if (response && response.data) {
        console.log('🔍 [DEBUG] Component: Stats response has data property, using it');
        setStats(response.data || {});
      } else {
        console.log('🔍 [DEBUG] Component: Stats response is direct object, using it');
        setStats(response || {});
      }
    } catch (err) {
      console.error('🔍 [DEBUG] Component: Error fetching stats:', err);
    }
  }, []);

  // Load data on component mount and filter changes
  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Handle filter changes
  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filters change
  }, []);

  // Handle pagination
  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  // Handle CSV export
  const handleExportCSV = useCallback(() => {
    try {
      exportTableToCSV(
        columns,
        matches,
        `founder-funder-matches-${format(new Date(), "yyyy-MM-dd")}`
      );
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setSnackbar({ open: true, message: 'Failed to export CSV', type: 'error' });
    }
  }, [matches]);

  // Handle match status update
  const handleStatusUpdate = useCallback(async (matchId, newStatus) => {
    try {
      await updateFounderFunderMatchAdmin(matchId, { status: newStatus }, authUser);
      setSnackbar({ open: true, message: 'Status updated successfully', type: 'success' });
      fetchMatches(); // Refresh data
    } catch (err) {
      console.error('Error updating status:', err);
      setSnackbar({ open: true, message: 'Failed to update status', type: 'error' });
    }
  }, [fetchMatches]);

  // Handle investment details update
  const handleInvestmentUpdate = useCallback(async (matchId, investmentData) => {
    try {
      await addInvestmentDetailsAdmin(matchId, investmentData, authUser);
      setSnackbar({ open: true, message: 'Investment details updated', type: 'success' });
      setInvestmentDialogOpen(false);
      fetchMatches(); // Refresh data
    } catch (err) {
      console.error('Error updating investment details:', err);
      setSnackbar({ open: true, message: 'Failed to update investment details', type: 'error' });
    }
  }, [fetchMatches]);

  // Handle communication tracking
  const handleTrackCommunication = useCallback(async (matchId) => {
    try {
      await trackCommunicationAdmin(matchId, authUser);
      setSnackbar({ open: true, message: 'Communication tracked', type: 'success' });
      fetchMatches(); // Refresh data
    } catch (err) {
      console.error('Error tracking communication:', err);
      setSnackbar({ open: true, message: 'Failed to track communication', type: 'error' });
    }
  }, [fetchMatches]);

  // Table columns configuration
  const columns = useMemo(() => [
    {
      header: "Match ID",
      accessor: "id",
      align: "left",
      render: (value) => (
        <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {value?.substring(0, 8)}...
        </Typography>
      ),
    },
    {
      header: "Founder",
      accessor: "founder",
      align: "left",
      render: (value) => (
        <Box>
          <Typography variant="body2">
            {value ? `${value.firstName} ${value.lastName}` : 'N/A'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {value?.email || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      header: "Funder",
      accessor: "funder",
      align: "left",
      render: (value) => (
        <Box>
          <Typography variant="body2">
            {value ? `${value.firstName} ${value.lastName}` : 'N/A'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {value?.email || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      header: "Campaign",
      accessor: "campaign",
      align: "left",
      render: (value) => (
        <Typography variant="body2">
          {value?.name || 'N/A'}
        </Typography>
      ),
    },
    {
      header: "Match Score",
      accessor: "matchScore",
      align: "center",
      render: (value) => (
        <Chip
          label={value ? `${value}%` : 'N/A'}
          color={value >= 80 ? 'success' : value >= 60 ? 'primary' : 'default'}
          size="small"
        />
      ),
    },
    {
      header: "Status",
      accessor: "status",
      align: "center",
      render: (value) => {
        const status = statusMap[value] || { label: value, color: 'default' };
        return (
          <Chip
            label={status.label}
            color={status.color}
            size="small"
          />
        );
      },
    },
    {
      header: "Connection Method",
      accessor: "connectionMethod",
      align: "center",
      render: (value) => {
        const method = connectionMethodMap[value] || { label: value, color: 'default' };
        return (
          <Chip
            label={method.label}
            color={method.color}
            size="small"
          />
        );
      },
    },
    {
      header: "Investment Stage",
      accessor: "investmentStage",
      align: "center",
      render: (value) => {
        if (!value) return <Typography variant="body2">N/A</Typography>;
        const stage = investmentStageMap[value] || { label: value, color: 'default' };
        return (
          <Chip
            label={stage.label}
            color={stage.color}
            size="small"
          />
        );
      },
    },
    {
      header: "Investment Amount",
      accessor: "investmentAmount",
      align: "right",
      render: (value) => (
        <Typography variant="body2">
          {value ? `$${value.toLocaleString()}` : 'N/A'}
        </Typography>
      ),
    },
    {
      header: "Communication",
      accessor: "communicationCount",
      align: "center",
      render: (value) => (
        <Typography variant="body2">
          {value || 0}
        </Typography>
      ),
    },
    {
      header: "Matched Date",
      accessor: "matchedAt",
      align: "center",
      render: (value) => (
        <Typography variant="body2">
          {value ? format(new Date(value), "MMM dd, yyyy") : 'N/A'}
        </Typography>
      ),
    },
    {
      header: "Actions",
      accessor: "actions",
      align: "center",
      render: (value, row) => (
        <Box display="flex" gap={1}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedMatch(row);
                setDetailDialogOpen(true);
              }}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Match">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedMatch(row);
                setEditDialogOpen(true);
              }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], []);

  // Overview metrics cards
  const OverviewCards = useMemo(() => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <Card>
        <div className="p-4 text-center">
          <Typography variant="h4" color="primary" style={{ fontWeight: 600 }}>
            {stats.totalMatches || 0}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Total Matches
          </Typography>
        </div>
      </Card>
      <Card>
        <div className="p-4 text-center">
          <Typography variant="h4" color="primary" style={{ fontWeight: 600 }}>
            {stats.totalConnected || 0}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Connected
          </Typography>
        </div>
      </Card>
      <Card>
        <div className="p-4 text-center">
          <Typography variant="h4" color="primary" style={{ fontWeight: 600 }}>
            {stats.connectionSuccessRate ? `${stats.connectionSuccessRate.toFixed(1)}%` : '0%'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Success Rate
          </Typography>
        </div>
      </Card>
      <Card>
        <div className="p-4 text-center">
          <Typography variant="h4" color="primary" style={{ fontWeight: 600 }}>
            ${stats.totalInvestmentValue ? stats.totalInvestmentValue.toLocaleString() : '0'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Total Investment Value
          </Typography>
        </div>
      </Card>
    </div>
  ), [stats]);

  // Filter section
  const FilterSection = useMemo(() => (
    <Card className="mb-4">
      <CardHeader>
        <span className="text-lg">Filters</span>
        <div className="flex items-center gap-2">
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setFilters({
                status: '',
                connectionMethod: '',
                investmentStage: '',
                matchedAtFrom: '',
                matchedAtTo: '',
                minMatchScore: '',
                maxMatchScore: ''
              });
              setPage(0);
            }}
          >
            Clear Filters
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={fetchMatches}
            startIcon={<Refresh />}
          >
            Refresh
          </Button>
        </div>
      </CardHeader>
      <div className="p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <FormControl fullWidth size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            label="Status"
          >
            <MenuItem value="">All Statuses</MenuItem>
            {Object.entries(statusMap).map(([key, { label }]) => (
              <MenuItem key={key} value={key}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel>Connection Method</InputLabel>
          <Select
            value={filters.connectionMethod}
            onChange={(e) => handleFilterChange('connectionMethod', e.target.value)}
            label="Connection Method"
          >
            <MenuItem value="">All Methods</MenuItem>
            {Object.entries(connectionMethodMap).map(([key, { label }]) => (
              <MenuItem key={key} value={key}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel>Investment Stage</InputLabel>
          <Select
            value={filters.investmentStage}
            onChange={(e) => handleFilterChange('investmentStage', e.target.value)}
            label="Investment Stage"
          >
            <MenuItem value="">All Stages</MenuItem>
            {Object.entries(investmentStageMap).map(([key, { label }]) => (
              <MenuItem key={key} value={key}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Min Match Score"
          type="number"
          value={filters.minMatchScore}
          onChange={(e) => handleFilterChange('minMatchScore', e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          inputProps={{ min: 0, max: 100 }}
        />

        <TextField
          label="Max Match Score"
          type="number"
          value={filters.maxMatchScore}
          onChange={(e) => handleFilterChange('maxMatchScore', e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          inputProps={{ min: 0, max: 100 }}
        />

        <TextField
          label="Matched From Date"
          type="date"
          value={filters.matchedAtFrom}
          onChange={(e) => handleFilterChange('matchedAtFrom', e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Matched To Date"
          type="date"
          value={filters.matchedAtTo}
          onChange={(e) => handleFilterChange('matchedAtTo', e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      </div>
    </Card>
  ), [filters, handleFilterChange, fetchMatches]);

  return (
    <Main>
      <Box display="flex" justifyContent="space-between" alignItems="center" p={4} borderBottom="1px solid rgb(239 232 251)">
        <Box>
          <Typography component="h2" variant="h6">
            Founder-Funder Matches
          </Typography>
          {authUser?.roles?.includes('TENANT_ADMIN') && (
            <Typography variant="caption" color="textSecondary">
              Tenant Admin View - Showing data for Tenant ID: {authUser.tenantId}
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<GetApp />}
            onClick={handleExportCSV}
            disabled={matches.length === 0}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => {
              // TODO: Implement create match functionality
              setSnackbar({ open: true, message: 'Create match functionality coming soon', type: 'info' });
            }}
          >
            Create Match
          </Button>
        </Box>
      </Box>

      <Box p={4}>
        {OverviewCards}
        {FilterSection}

        <Card>
          <CardContent>
            {loading && <Typography>Loading...</Typography>}
                         {error && (
               <Box mb={2} p={2} bgcolor="error.light" borderRadius={1}>
                 <Typography color="error" variant="body2">
                   {error}
                 </Typography>
                 <Button 
                   variant="contained" 
                   size="small" 
                   onClick={() => window.location.reload()}
                   style={{ marginTop: 8 }}
                 >
                   Retry
                 </Button>
               </Box>
             )}
            
            {!loading && !error && (
              <>
                {console.log('🔍 [DEBUG] Component: Rendering table with:', { 
                  matchesLength: matches.length, 
                  totalCount, 
                  matches: matches.slice(0, 2) // Log first 2 matches for debugging
                })}
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {columns.map((column, index) => (
                          <TableCell key={index} align={column.align || 'left'}>
                            {column.header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {matches.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={columns.length} align="center">
                            No matches found
                          </TableCell>
                        </TableRow>
                      ) : (
                        matches.map((match) => (
                          <TableRow key={match.id}>
                            {columns.map((column, index) => (
                              <TableCell key={index} align={column.align || 'left'}>
                                {column.render ? column.render(match[column.accessor], match) : match[column.accessor]}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handlePageChange}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Alert for notifications */}
      {snackbar.open && (
        <Box position="fixed" top={16} right={16} zIndex={9999}>
          <Alert 
            severity={snackbar.type} 
            variant="filled"
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            {snackbar.message}
          </Alert>
        </Box>
      )}
    </Main>
  );
}
